import React, { useReducer, useState, useEffect } from "react";
import PropTypes from "prop-types";
import ShippingTable from "../../elements/Tables/ShippingTable/ShippingTable";
// import ShippingCreationForm from "../../elements/Forms/ShippingCreationForm/ShippingCreationForm";

import { useFetch } from "../../../Utils/useFetch";

import { FETCH_RETURN_PRINCIPLE_SHIPPING_API } from "../../../Constants/Apis";
import {
  Button,
  Flex,
  Input,
  message,
  Modal,
  DatePicker,
  Card,
  Statistic,
} from "antd";
import * as XLSX from "xlsx";
import { reducer } from "../../../Utils/CommonUtils";
import { deleteShipping } from "../../../Services/ShippingService";
import ReturnPrincipleDashboardTable from "../../elements/Tables/ShippingDashboardTable/ReturnPrincipleDashboardTable";
import dayjs from "dayjs";

const { Search } = Input;
const { RangePicker } = DatePicker;

const ReturnPrincipleDashboardPage = ({ history, userData }) => {
  const initialState = {
    searchKeyword: "",
    dateRange: {
      startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
    },
    sorter: {},
    current: 1,
    pageSize: 50,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shippingId, setShippingId] = useState(null);
  const [selectedNoSJ, setSelectedNoSJ] = useState(null);
  const [dateRange, setDateRange] = useState([]);

  const handleOpenModal = () => setIsModalVisible(true);
  const handleCloseModal = () => setIsModalVisible(false);

  const handleDelete = async (id) => {
    setShippingId(id);
    setIsModalOpen(true);
  };

  const handleCreate = async () => {
    history.push("/user/create-shipping");
  };

  const handleExport = () => {
    if (!stockList?.length) {
      message.warning("No data to export");
      return;
    }

    const headerRows = [
      {
        "Date Range":
          state.dateRange.startDate && state.dateRange.endDate
            ? `${state.dateRange.startDate} to ${state.dateRange.endDate}`
            : "All Dates",
      },
      {},
      {
        "Tanggal Kirim": "Tanggal Kirim",
        "Tanggal Terima ETA": "Tanggal Terima ETA",
        "No. Nota retur": "No. Nota retur",
        "No. Nota penjualan": "No. Nota penjualan",
        Status: "Status",
        "Kode outlet": "Kode outlet",
        Destination: "Destination",
        "Total Qty": "Total Qty",
        "Total Cost": "Total Cost",
        "Alasan retur": "Alasan retur",
      },
    ];

    const formattedData = stockList.map((item) => ({
      "Tanggal Kirim": item.tanggal_kirim
        ? new Date(item.tanggal_kirim).toLocaleDateString("en-GB")
        : "N/A",
      "Tanggal Terima ETA": item.tanggal_terima_eta
        ? new Date(item.tanggal_terima_eta).toLocaleDateString("en-GB")
        : "N/A",
      "No. Nota retur": item.noForm,
      "No. Nota penjualan": item.noSJ,
      Status: item.status,
      "Kode outlet": item.sender,
      Destination: item.destination,
      "Total Qty": item.totalQuantity,
      "Total Cost": item.totalCost,
      "Alasan retur": item.keterangan,
    }));

    // Combine header rows with data
    const allData = [...headerRows, ...formattedData];

    const worksheet = XLSX.utils.json_to_sheet(allData, { skipHeader: true });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Shipping Data");
    XLSX.writeFile(workbook, "Shipping_Data.xlsx");
  };

  const handleOk = async () => {
    try {
      await deleteShipping(shippingId);
      message.success("Shipping deleted successfully");
      callReFetchStock();
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
        "Something went wrong, failed to delete shipping."
      );
    }
    setIsModalOpen(false);
  };

  const apiUrl = FETCH_RETURN_PRINCIPLE_SHIPPING_API;
  const apiParams = {
    ...(state.dateRange?.startDate && { startDate: state.dateRange.startDate }),
    ...(state.dateRange?.endDate && { endDate: state.dateRange.endDate }),
    ...(state.searchKeyword && { searchKeyword: state.searchKeyword }),
    current: state.current || 1,
    pageSize: state.pageSize || 50,
  };

  const {
    fetchedData: { data: responseData = {} },
    callReFetch: callReFetchStock,
    loading: loadingStock,
  } = useFetch(apiUrl, apiParams);

  // API returns { success: true, data: { data: [...], pagination: {...} } }
  const stockList =
    responseData?.data?.data ??
    (Array.isArray(responseData?.data) ? responseData.data : []);
  const tableData = responseData?.data ?? { data: stockList };

  useEffect(() => {
    if (selectedNoSJ) {
      callReFetchStock();
    }
  }, [selectedNoSJ]);

  const handleSearch = (value) => {
    dispatch({ type: "searchKeyword", payload: value });
    callReFetchStock();
  };

  const handleDateChange = (dates) => {
    const [startDate, endDate] = dates || [];
    setDateRange(dates);

    const formattedStartDate = startDate
      ? startDate.format("YYYY-MM-DD")
      : null;
    const formattedEndDate = endDate ? endDate.format("YYYY-MM-DD") : null;

    const range = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };

    dispatch({ type: "dateRange", payload: range });
    callReFetchStock();
  };

  // Calculate aggregated data
  const lateCount =
    stockList.filter((item) => item.days_until_eta < 0).length || 0;
  const inDeliveryCount =
    stockList.filter((item) => item.status === "in delivery").length ||
    0;
  const totalDifference =
    stockList.reduce(
      (sum, item) =>
        item.status === "in stock" ? sum + (item.quantity_diff || 0) : sum,
      0
    ) || 0;

  return (
    <div
      style={{
        width: "100%",
        padding: "20px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowX: "hidden",
      }}
    >
      <h1>Return Dashboard (Principle)</h1>
      <div
        style={{
          paddingBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1200px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Flex gap="small">
          <Button type="default" onClick={handleExport}>
            Export to Excel
          </Button>
          <RangePicker
            style={{ width: "100%" }}
            onChange={handleDateChange}
            defaultValue={[
              dayjs(state.dateRange.startDate),
              dayjs(state.dateRange.endDate),
            ]}
          />
          <Search
            placeholder="Search..."
            allowClear
            onSearch={handleSearch}
            style={{ width: "300px" }}
          />
        </Flex>
      </div>

      <ReturnPrincipleDashboardTable
        history={history}
        loading={loadingStock}
        data={tableData}
        callReFetch={callReFetchStock}
        dispatch={dispatch}
      />

      <Modal
        title="Delete Shipping"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
      >
        <p>Are you sure you want to delete this shipping?</p>
      </Modal>
    </div>
  );
};

ReturnPrincipleDashboardPage.propTypes = {};

export default ReturnPrincipleDashboardPage;
